import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import Pagination from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function UserTickets() {
  const navigate = useNavigate();
  const location = useLocation();
  const ticketBase = location.pathname.startsWith("/revenda") ? "/revenda" : "/painel";
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 12 });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "Ativação", message: "" });
  const limit = 12;

  const load = (p = page, term = q) => {
    const params = new URLSearchParams({ page: p, limit });
    if (term.trim()) params.set("q", term.trim());
    return api.get(`/support/tickets?${params}`).then(({ data }) => setData(data)).catch(() => {});
  };
  useEffect(() => { load(page, q); /* eslint-disable-next-line */ }, [page]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1, q); }, 300);
    return () => clearTimeout(t); /* eslint-disable-next-line */
  }, [q]);

  const create = async () => {
    if (!form.subject.trim() || !form.message.trim()) return toast.error("Preencha assunto e mensagem.");
    try {
      await api.post("/support/tickets", form);
      toast.success("Ticket criado!");
      setOpen(false);
      setForm({ subject: "", category: "Ativação", message: "" });
      load(1, "");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="user-tickets">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Suporte</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-1" data-testid="ticket-new-btn"><Plus className="h-4 w-4" /> Novo ticket</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Abrir ticket</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Assunto</Label><Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} data-testid="ticket-subject-input" /></div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="ticket-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ativação">Ativação</SelectItem>
                    <SelectItem value="Créditos">Créditos</SelectItem>
                    <SelectItem value="Conta">Conta</SelectItem>
                    <SelectItem value="Geral">Geral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Mensagem</Label><Textarea rows={4} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} data-testid="ticket-message-input" /></div>
              <Button className="w-full" onClick={create} data-testid="ticket-create-submit">Criar ticket</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative w-full sm:w-80">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por assunto..." className="pl-9" data-testid="tickets-search-input" />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.items.length === 0 && <p className="col-span-full rounded-lg border border-dashed border-border py-12 text-center text-muted-foreground">Nenhum ticket ainda.</p>}
        {data.items.map((t) => (
          <button key={t.id} onClick={() => navigate(`${ticketBase}/tickets/${t.id}`)} className="rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-primary/50" data-testid={`ticket-card-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <span className="font-medium">{t.subject}</span>
              <StatusBadge status={t.status} />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{t.category} · {new Date(t.updated_at).toLocaleDateString("pt-BR")}</p>
          </button>
        ))}
      </div>

      <Pagination page={data.page} limit={data.limit} total={data.total} onPage={setPage} label="tickets" testid="tickets-pager" />
    </div>
  );
}
